import { HlcSchema } from '@dl/shared';
import { z } from 'zod';

import { OpRecordSchema } from './op-record.js';
import { SubmittedOpSchema } from './submitted-op.js';

/**
 * Sync protocol wire envelopes. Versioned at the top level so we can
 * evolve the protocol without breaking older clients. Per 11-tech-stack.md
 * §6, the wire format is JSON (versioned, with MessagePack as a future
 * upgrade path).
 *
 * **Version history**
 *
 * - `1` — Walking Skeleton. GM-only client. Server broadcasts raw `op_push`
 *   to every connected socket.
 * - `2` — Phase 1 wire-level projection (III.A.1-4 / #61-#64). Server
 *   invokes the projection chokepoint per (viewer, t) before broadcasting
 *   to non-GM viewers; GM viewers still receive raw `op_push` via the
 *   identity-projection code path. Adds two new server envelopes —
 *   `visibility_added` and `visibility_removed` — carrying projected
 *   `VisibleFact` deltas (the latter omits the value, see #63). Adds an
 *   optional `watermark` field on `hello` to resume a per-(Campaign,
 *   Viewer) stream across reconnects (#62).
 */
export const SYNC_PROTOCOL_VERSION = 2 as const;

const PayloadAny = z.unknown();

/** Client → server: initial handshake. */
export const HelloEnvelopeSchema = z
  .object({
    kind: z.literal('hello'),
    protocol_version: z.literal(SYNC_PROTOCOL_VERSION),
    /**
     * Last HLC the client has seen accepted; null if first-ever connect.
     *
     * For non-GM viewers this is INTERPRETED ALONGSIDE the per-Viewer
     * watermark the server has persisted in DOSQL `viewer_watermarks`
     * (#62). If the client carries a more advanced HLC than the server's
     * stored watermark (e.g. a stale local store), the server resumes
     * from the more-advanced of the two so the client never sees an op
     * it has already applied.
     *
     * Per-(Campaign, Viewer).
     */
    last_seen_hlc: HlcSchema.nullable(),
    /**
     * Campaign id the client wants to sync against. The Worker route uses
     * this to look up the DO id. Authentication (signed `ws_token`,
     * I.C.1) lives in the Worker gate; the DO trusts the
     * Worker-forwarded `x-dl-user-id` / `x-dl-viewer-*` headers.
     */
    campaign_id: z.string().min(1),
  })
  .strict();
export type HelloEnvelope = z.infer<typeof HelloEnvelopeSchema>;

/** Server → client: acknowledges the handshake. Backfill begins right after. */
export const AckEnvelopeSchema = z
  .object({
    kind: z.literal('ack'),
    /** Server's current HLC at handshake time. */
    server_hlc: HlcSchema,
  })
  .strict();
export type AckEnvelope = z.infer<typeof AckEnvelopeSchema>;

/** Client → server: submit a local op for arbitration. */
export const OpSubmitEnvelopeSchema = z
  .object({
    kind: z.literal('op'),
    /** Client's provisional id (so accept/reject can be correlated). */
    client_op_id: z.string().min(1),
    op: SubmittedOpSchema(PayloadAny),
  })
  .strict();
export type OpSubmitEnvelope = z.infer<typeof OpSubmitEnvelopeSchema>;

/** Server → client: the submitted op was accepted; here's the canonical record. */
export const OpAcceptEnvelopeSchema = z
  .object({
    kind: z.literal('op_accept'),
    client_op_id: z.string().min(1),
    op: OpRecordSchema(PayloadAny),
  })
  .strict();
export type OpAcceptEnvelope = z.infer<typeof OpAcceptEnvelopeSchema>;

/** Server → client: base_version was stale. Surface as draft-with-conflict. */
export const OpRejectStaleEnvelopeSchema = z
  .object({
    kind: z.literal('op_reject_stale'),
    client_op_id: z.string().min(1),
    current_head: z.string().min(1),
    current_op: OpRecordSchema(PayloadAny),
  })
  .strict();
export type OpRejectStaleEnvelope = z.infer<typeof OpRejectStaleEnvelopeSchema>;

/**
 * Server → client: the submitted op violated a structural invariant (e.g.
 * an LLM-authored fact.create with status='pending'/'canon', I.A.5 / #97).
 * Distinct from `op_reject_stale` (causal conflict, surfaced as
 * draft-with-conflict) — an invariant rejection means the client construct
 * itself was malformed and is NOT retryable as-is. `code` is a stable
 * machine-readable tag; `message` is GM-readable but never user-rendered
 * directly (the UI maps `code` to its own i18n string).
 */
export const OpRejectInvariantEnvelopeSchema = z
  .object({
    kind: z.literal('op_reject_invariant'),
    client_op_id: z.string().min(1),
    code: z.string().min(1),
    message: z.string().min(1),
  })
  .strict();
export type OpRejectInvariantEnvelope = z.infer<typeof OpRejectInvariantEnvelopeSchema>;

/** Server → client: push an op accepted by some other client / system. */
export const OpPushEnvelopeSchema = z
  .object({
    kind: z.literal('op_push'),
    op: OpRecordSchema(PayloadAny),
  })
  .strict();
export type OpPushEnvelope = z.infer<typeof OpPushEnvelopeSchema>;

/**
 * Server → client (non-GM): a `VisibleFact` has appeared in the Viewer's
 * projection — either because a new fact landed on a chain the Viewer
 * already has a reveal for, or because a reveal op now exposes a fact
 * the Viewer didn't see before (#63).
 *
 * The shape is by-value: `{subject, predicate, value, pending?}` matching
 * `packages/projection`'s `VisibleFact` exactly. Construction is done
 * inside the projection chokepoint; this envelope is the wire-level
 * carrier. The Worker (do-workspace) is the ONLY server-side caller of
 * the chokepoint — the CI grep enforces that.
 *
 * Carries the server HLC at which the visibility was computed so the
 * client can advance its per-Viewer watermark atomically with delivery
 * (#62).
 *
 * `via_reveal_op` is an optional debug / UX hint identifying the reveal
 * op that caused this visibility. Renderers MAY display "newly revealed"
 * affordances keyed off this id; the field is OPTIONAL and clients MUST
 * NOT rely on it being present (e.g. catch-up replays may omit it).
 */
export const VisibilityAddedEnvelopeSchema = z
  .object({
    kind: z.literal('visibility_added'),
    subject: z.string().min(1),
    predicate: z.string().min(1),
    /**
     * Projected value (post-reveal-filter / status-filter / validity-filter
     * / owner-filter). Sensitive content from un-revealed facts NEVER
     * reaches this field — the projection chokepoint is the gate.
     */
    value: z.unknown(),
    /** Present and `true` iff the folded status is `pending` (I.A.5). */
    pending: z.literal(true).optional(),
    /**
     * Chain head op-id — the id whose value this is (== `currentHeadId`
     * server-side). A non-GM client submits it as `base_version` for
     * conflict-aware edits (§7.2). No extra disclosure: the value is already
     * visible. Absent for facts with no resolvable head.
     */
    version: z.string().min(1).optional(),
    /** Server HLC at which the visibility was computed. */
    hlc: HlcSchema,
    /** Optional debug hint: the reveal op id that surfaced this fact. */
    via_reveal_op: z.string().min(1).optional(),
  })
  .strict();
export type VisibilityAddedEnvelope = z.infer<typeof VisibilityAddedEnvelopeSchema>;

/**
 * Server → client (non-GM): a `VisibleFact` has been REMOVED from the
 * Viewer's projection — typically because an `unreveal` op was appended
 * (#63), or because a `fact.soft_delete` / status flip dropped the fact
 * out of the projection's status / validity / owner filter.
 *
 * **Critically**, this envelope carries ONLY the fact id (subject +
 * predicate). It NEVER carries the value or any other payload field.
 * The phantom-Viewer wire-level test (#64) asserts that no value from
 * an un-revealed fact appears in any `visibility_removed` frame.
 *
 * Per `02-architecture.md §4.9`, reveals are write-once. `unreveal` is
 * still in the op shape vocabulary for forward compatibility (snapshot
 * restore, pack invalidation, …) — the spec says emit
 * `visibility_removed` when it occurs; we implement and document.
 */
export const VisibilityRemovedEnvelopeSchema = z
  .object({
    kind: z.literal('visibility_removed'),
    subject: z.string().min(1),
    predicate: z.string().min(1),
    /** Server HLC at which the removal was computed. */
    hlc: HlcSchema,
    /** Optional debug hint: the op id that caused the removal. */
    via_op: z.string().min(1).optional(),
  })
  .strict();
export type VisibilityRemovedEnvelope = z.infer<typeof VisibilityRemovedEnvelopeSchema>;

/** Server → client: catch-up watermark for ordering / progress UX. */
export const WatermarkEnvelopeSchema = z
  .object({
    kind: z.literal('watermark'),
    server_hlc: HlcSchema,
  })
  .strict();
export type WatermarkEnvelope = z.infer<typeof WatermarkEnvelopeSchema>;

/** Bidirectional keepalive. */
export const PingEnvelopeSchema = z.object({ kind: z.literal('ping') }).strict();
export const PongEnvelopeSchema = z.object({ kind: z.literal('pong') }).strict();
export type PingEnvelope = z.infer<typeof PingEnvelopeSchema>;
export type PongEnvelope = z.infer<typeof PongEnvelopeSchema>;

// ADR-0005 §"Session cursor": the ephemeral `session_cursor_set` /
// `session_cursor_changed` relay was removed. "Now" is no longer a
// presence-style side channel — it rides the op log as a
// `config.set session_cursor` op (LWW), so a co-GM's advance reaches every
// connected client through the normal op-push broadcast + fold, and the value
// is op-log-canonical + replay-safe (no out-of-band D1 source of truth).

/** All client → server envelopes. */
export const ClientEnvelopeSchema = z.discriminatedUnion('kind', [
  HelloEnvelopeSchema,
  OpSubmitEnvelopeSchema,
  PingEnvelopeSchema,
  PongEnvelopeSchema,
]);
export type ClientEnvelope = z.infer<typeof ClientEnvelopeSchema>;

/** All server → client envelopes. */
export const ServerEnvelopeSchema = z.discriminatedUnion('kind', [
  AckEnvelopeSchema,
  OpAcceptEnvelopeSchema,
  OpRejectStaleEnvelopeSchema,
  OpRejectInvariantEnvelopeSchema,
  OpPushEnvelopeSchema,
  WatermarkEnvelopeSchema,
  VisibilityAddedEnvelopeSchema,
  VisibilityRemovedEnvelopeSchema,
  PingEnvelopeSchema,
  PongEnvelopeSchema,
]);
export type ServerEnvelope = z.infer<typeof ServerEnvelopeSchema>;
